import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { aud } from "@/lib/api";
import { STATES, SALE_TIMING } from "./questions";

export default function PreviewGate({ preview, onSubmit, submitting }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    mobile: "",
    state: "",
    suburb: "",
    sale_timing: "",
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name";
    if (!form.last_name.trim()) e.last_name = "Please enter your last name";
    if (!form.business_name.trim()) e.business_name = "Please enter your salon name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!form.mobile.trim()) e.mobile = "Please enter your mobile number";
    if (!form.state) e.state = "Please select your state";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (validate()) onSubmit(form);
  };

  const field =
    "w-full bg-transparent border-b-2 border-border focus:border-brand outline-none py-3 text-lg transition-colors placeholder:text-muted-foreground/40";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl"
      data-testid="preview-gate"
    >
      <div className="eyebrow text-brand mb-5">Your salon valuation is ready</div>
      <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tight mb-10">
        Enter your details to
        <br />
        unlock your full result
      </h2>

      {/* Preview facts */}
      <div className="grid sm:grid-cols-3 border-t border-l border-border mb-6">
        <div className="border-b border-r border-border p-5">
          <div className="eyebrow text-muted-foreground mb-2">Owner earnings (SDE)</div>
          <div className="font-serif text-3xl">{aud(preview?.sde)}</div>
        </div>
        <div className="border-b border-r border-border p-5">
          <div className="eyebrow text-muted-foreground mb-2">Station use</div>
          <div className="font-serif text-3xl">{preview?.station_utilisation_text?.match(/\((\d+%)\)/)?.[1] || "—"}</div>
        </div>
        <div className="border-b border-r border-border p-5">
          <div className="eyebrow text-muted-foreground mb-2">Indicative value</div>
          <div className="relative font-serif text-3xl">
            <span className="blur-md select-none" data-testid="blurred-value">
              {aud(preview?.recommended_value)}
            </span>
            <Lock size={18} className="absolute right-0 top-1/2 -translate-y-1/2 text-brand" />
          </div>
        </div>
      </div>

      <p className="text-muted-foreground mb-8 max-w-xl">{preview?.lease_assessment}</p>

      {/* Lead form */}
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="lead-form">
        <div>
          <label className="eyebrow text-muted-foreground">First name*</label>
          <input
            data-testid="lead-first-name"
            className={field}
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            placeholder="Alex"
          />
          {errors.first_name && <p className="text-destructive text-sm mt-1">{errors.first_name}</p>}
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Last name*</label>
          <input
            data-testid="lead-last-name"
            className={field}
            value={form.last_name}
            onChange={(e) => set("last_name", e.target.value)}
            placeholder="Taylor"
          />
          {errors.last_name && <p className="text-destructive text-sm mt-1">{errors.last_name}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow text-muted-foreground">Salon / business name*</label>
          <input
            data-testid="lead-business-name"
            className={field}
            value={form.business_name}
            onChange={(e) => set("business_name", e.target.value)}
            placeholder="e.g. Luxe Hair Studio"
          />
          {errors.business_name && <p className="text-destructive text-sm mt-1">{errors.business_name}</p>}
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Email*</label>
          <input
            data-testid="lead-email"
            className={field}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@email.com"
          />
          {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Mobile*</label>
          <input
            data-testid="lead-mobile"
            className={field}
            value={form.mobile}
            onChange={(e) => set("mobile", e.target.value)}
            placeholder="04xx xxx xxx"
          />
          {errors.mobile && <p className="text-destructive text-sm mt-1">{errors.mobile}</p>}
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">State*</label>
          <Select value={form.state} onValueChange={(v) => set("state", v)}>
            <SelectTrigger
              data-testid="lead-state"
              className="w-full bg-transparent border-0 border-b-2 border-border rounded-none px-0 py-3 h-auto text-lg focus:ring-0 focus:border-brand"
            >
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {STATES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`state-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && <p className="text-destructive text-sm mt-1">{errors.state}</p>}
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Suburb</label>
          <input
            data-testid="lead-suburb"
            className={field}
            value={form.suburb}
            onChange={(e) => set("suburb", e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">When might you consider selling?</label>
          <Select value={form.sale_timing} onValueChange={(v) => set("sale_timing", v)}>
            <SelectTrigger
              data-testid="lead-timing"
              className="w-full bg-transparent border-0 border-b-2 border-border rounded-none px-0 py-3 h-auto text-lg focus:ring-0 focus:border-brand"
            >
              <SelectValue placeholder="Select timing" />
            </SelectTrigger>
            <SelectContent>
              {SALE_TIMING.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`timing-${t.value}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2 mt-2">
          <button
            data-testid="unlock-button"
            type="submit"
            disabled={submitting}
            className="group inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground pl-8 pr-6 py-5 eyebrow disabled:opacity-50 hover:bg-brand transition-colors duration-300"
          >
            {submitting ? "Unlocking\u2026" : "Unlock Full Report"}
            <span className="grid place-items-center w-8 h-8 rounded-full bg-white/20 transition-transform duration-300 group-hover:translate-x-1">
              <ArrowRight size={16} />
            </span>
          </button>
          <p className="text-xs text-muted-foreground mt-4 max-w-md">
            We&rsquo;ll email a copy of your report. Your details stay confidential
            and there is no obligation.
          </p>
        </div>
      </form>
    </motion.div>
  );
}
